import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  X,
  FolderOpen,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
  RefreshCw,
  FolderInput,
} from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { ProjectSummary } from "@/types";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge, type BadgeVariant } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Select } from "@/components/ui/select";
import { Tooltip } from "@/components/ui/tooltip";
import {
  sortOptionsByLabel,
  sortPhaseOptions,
  sortTextOptions,
} from "@/lib/filter-options";
import { useProjects } from "@/hooks/useProjects";
import { BurnRateCell } from "@/components/projects/BurnRateCell";
import { PendingApprovalSection } from "@/components/projects/PendingApprovalSection";
import { MigrateProjectDialog } from "@/components/projects/migration/MigrateProjectDialog";
import { phaseLabel } from "@/lib/phases";
import { projectTypeLabel } from "@/lib/project-types";

/** Roles allowed to migrate an existing project (mirrors migrate-project.ts withRoles). */
const MIGRATE_ROLES: ReadonlySet<string> = new Set([
  "pm",
  "sa",
  "leadership",
  "admin",
]);

type SortKey =
  | "title"
  | "current_phase"
  | "project_type"
  | "project_manager"
  | "solution_architect"
  | "burn_rate_pct"
  | "status";
type SortDir = "asc" | "desc";

const ALL = "all";

/**
 * Filter value sets. Declared in source order and ordered for display by the `sortOptionsByLabel` /
 * `sortPhaseOptions` helpers at the call site — never hand-ordered, so a value added here cannot
 * land out of order.
 *
 * `pending_approval` and `in_review` are stored values, not display labels — they were live in the
 * data and referenced elsewhere on this page (the Pending Review section filters on `in_review`)
 * but were missing from the dropdown, so neither was selectable. `STATUS_FILTER_LABELS` keeps the
 * sent value raw while the user reads prose.
 *
 * Selecting nothing ("All statuses") sends the explicit `ALL_STATUSES` signal from `useProjects` —
 * it now genuinely includes Closed, which it previously did not despite the label.
 */
const STATUS_FILTER_VALUES: readonly string[] = [
  "Active",
  "Closing",
  "Closed",
  "On Hold",
  "pending_approval",
  "in_review",
];

/** Display label per stored status value. Values absent here render as stored. */
const STATUS_FILTER_LABELS: Record<string, string> = {
  pending_approval: "Pending Approval",
  in_review: "In Review",
};

function statusLabel(status: string): string {
  return STATUS_FILTER_LABELS[status] ?? status;
}
const PHASE_FILTER_VALUES: readonly string[] = [
  "Phase 0",
  "Phase 1",
  "Phase 2",
  "Phase 3",
  "Phase 4",
];

/** Fixed width for the Project column so long titles cannot push the rest of the table around. */
const TITLE_COLUMN_CLASS = "w-[280px] max-w-[280px]";

const PHASE_VARIANT: Record<string, BadgeVariant> = {
  "Phase 0": "neutral",
  "Phase 1": "info",
  "Phase 2": "warning",
  "Phase 3": "warning",
  "Phase 4": "success",
};

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  Active: "success",
  Closing: "warning",
  Closed: "neutral",
  "On Hold": "info",
  pending_approval: "info",
  in_review: "warning",
};

function phaseVariant(phase: string): BadgeVariant {
  return PHASE_VARIANT[phase] ?? "neutral";
}

function statusVariant(status: string | null): BadgeVariant {
  if (!status) return "neutral";
  return STATUS_VARIANT[status] ?? "neutral";
}

function FilterSelect({
  value,
  onChange,
  placeholder,
  options,
  allLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  options: { value: string; label: string }[];
  allLabel: string;
}): JSX.Element {
  return (
    <Select
      value={value || ALL}
      onChange={(v) => onChange(v === ALL ? "" : v)}
      className="h-[42px] w-[150px]"
      aria-label={placeholder}
      placeholder={placeholder}
      options={[{ value: ALL, label: allLabel }, ...options]}
    />
  );
}

/**
 * Sortable column header. The active direction arrow uses full-contrast primary text at a heavier
 * stroke; the inactive hint is revealed only on hover/keyboard focus so the sorted column is the
 * only indicator visible at rest.
 */
function SortHeader({
  label,
  column,
  sortKey,
  sortDir,
  onSort,
  className,
}: {
  label: string;
  column: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  className?: string;
}): JSX.Element {
  const active = sortKey === column;
  return (
    <TableHead
      className={cn("select-none", className)}
      aria-sort={
        active ? (sortDir === "asc" ? "ascending" : "descending") : "none"
      }
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className="group flex w-full items-center gap-1 text-left uppercase tracking-[0.48px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
      >
        <span className={cn(active && "text-text-primary")}>{label}</span>
        {active ? (
          sortDir === "asc" ? (
            <ArrowUp
              className="h-3.5 w-3.5 shrink-0 text-text-primary"
              strokeWidth={3}
              aria-hidden="true"
            />
          ) : (
            <ArrowDown
              className="h-3.5 w-3.5 shrink-0 text-text-primary"
              strokeWidth={3}
              aria-hidden="true"
            />
          )
        ) : (
          <ChevronsUpDown
            className="h-3 w-3 shrink-0 text-text-placeholder opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
            aria-hidden="true"
          />
        )}
      </button>
    </TableHead>
  );
}

function ProjectsPage(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canApprove = user?.role === "admin" || user?.role === "leadership";
  const canMigrate = !!user?.role && MIGRATE_ROLES.has(user.role);
  const [showMigrate, setShowMigrate] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [phase, setPhase] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [pmFilter, setPmFilter] = useState("");
  const [saFilter, setSaFilter] = useState("");

  const [sortKey, setSortKey] = useState<SortKey>("title");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, isError, error, refetch, isFetching } = useProjects({
    search: debouncedSearch,
    status,
    phase,
    limit: 50,
  });

  const projects = useMemo<ProjectSummary[]>(
    () => data?.projects ?? [],
    [data],
  );

  // Pending Review (Feature 2): active projects whose status is 'in_review' — a human gate is open.
  // Derived from the already-loaded list (no gate data on the list endpoint).
  const inReviewProjects = useMemo<ProjectSummary[]>(
    () => projects.filter((p) => p.status === "in_review"),
    [projects],
  );

  const typeOptions = useMemo(
    () =>
      sortTextOptions([
        ...new Set(
          projects.map((p) => p.project_type).filter((v): v is string => !!v),
        ),
      ]),
    [projects],
  );
  const pmOptions = useMemo(
    () =>
      sortTextOptions([
        ...new Set(
          projects
            .map((p) => p.project_manager)
            .filter((v): v is string => !!v),
        ),
      ]),
    [projects],
  );
  const saOptions = useMemo(
    () =>
      sortTextOptions([
        ...new Set(
          projects
            .map((p) => p.solution_architect)
            .filter((v): v is string => !!v),
        ),
      ]),
    [projects],
  );

  const filtered = useMemo(() => {
    const result = projects.filter(
      (p) =>
        (!typeFilter || p.project_type === typeFilter) &&
        (!pmFilter || p.project_manager === pmFilter) &&
        (!saFilter || p.solution_architect === saFilter),
    );

    const dir = sortDir === "asc" ? 1 : -1;
    return [...result].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number")
        return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [projects, typeFilter, pmFilter, saFilter, sortKey, sortDir]);

  const activeCount = projects.filter((p) => p.status === "Active").length;
  const totalCount = data?.total_count ?? projects.length;
  const hasActiveFilters = Boolean(
    status || phase || typeFilter || pmFilter || saFilter || debouncedSearch,
  );

  const handleSort = (key: SortKey): void => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const clearFilters = (): void => {
    setSearch("");
    setStatus("");
    setPhase("");
    setTypeFilter("");
    setPmFilter("");
    setSaFilter("");
  };

  return (
    <div className="flex flex-col gap-6 p-0">
      {/* Page header */}
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-neutral-900">Projects</h1>
            <Badge variant="neutral">{totalCount}</Badge>
          </div>
          <p className="text-sm text-neutral-500">
            {activeCount} active in this view
          </p>
        </div>
        {canMigrate && (
          <Button className="gap-2" onClick={() => setShowMigrate(true)}>
            <FolderInput className="h-4 w-4" aria-hidden="true" />
            Migrate existing project
          </Button>
        )}
      </div>

      {canMigrate && (
        <MigrateProjectDialog
          open={showMigrate}
          onOpenChange={setShowMigrate}
        />
      )}

      {/* Pending Approval (HubSpot-sourced) — leadership/admin only */}
      {canApprove && <PendingApprovalSection />}

      {/* Pending Review — active projects with an open human gate (status = in_review) */}
      {inReviewProjects.length > 0 && (
        <section aria-label="Projects pending review" className="space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-text-primary">
              ⏳ Pending Review
            </h2>
            <Badge variant="warning">{inReviewProjects.length}</Badge>
          </div>
          <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project</TableHead>
                  <TableHead>Phase</TableHead>
                  <TableHead>PM</TableHead>
                  <TableHead className="w-24 text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inReviewProjects.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-text-primary">
                          {p.title}
                        </span>
                        <span className="font-mono text-xs text-text-secondary">
                          {p.jira_key}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={phaseVariant(p.current_phase)}>
                        {phaseLabel(p.current_phase)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-text-secondary">
                      {p.project_manager || "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/projects/${p.jira_key}`)}
                      >
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or Jira key…"
            aria-label="Search projects"
            className="h-[42px] pl-9 pr-9 bg-white border-outlines"
          />
          {search && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-text-secondary"
            >
              <X />
            </Button>
          )}
        </div>

        <FilterSelect
          value={status}
          onChange={setStatus}
          placeholder="Status"
          allLabel="All statuses"
          options={sortOptionsByLabel(
            STATUS_FILTER_VALUES.map((s) => ({
              value: s,
              label: statusLabel(s),
            })),
          )}
        />
        <FilterSelect
          value={phase}
          onChange={setPhase}
          placeholder="Phase"
          allLabel="All phases"
          options={sortPhaseOptions(PHASE_FILTER_VALUES).map((p) => ({
            value: p,
            label: phaseLabel(p),
          }))}
        />
        <FilterSelect
          value={typeFilter}
          onChange={setTypeFilter}
          placeholder="Type"
          allLabel="All types"
          options={sortOptionsByLabel(
            typeOptions.map((t) => ({ value: t, label: projectTypeLabel(t) })),
          )}
        />
        <FilterSelect
          value={pmFilter}
          onChange={setPmFilter}
          placeholder="PM"
          allLabel="All PMs"
          options={pmOptions.map((p) => ({ value: p, label: p }))}
        />
        <FilterSelect
          value={saFilter}
          onChange={setSaFilter}
          placeholder="SA"
          allLabel="All SAs"
          options={saOptions.map((s) => ({ value: s, label: s }))}
        />

        {hasActiveFilters && (
          <Button
            variant="ghost"
            className="h-[42px] gap-1.5"
            onClick={clearFilters}
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </Button>
        )}
      </div>

      {/* Error */}
      {isError && !isLoading && (
        <Alert variant="destructive">
          <AlertTitle>Failed to load projects</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {error instanceof Error
                ? error.message
                : "An unexpected error occurred."}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => refetch()}
            >
              <RefreshCw
                className={cn("h-3.5 w-3.5", isFetching && "animate-spin")}
              />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Loading */}
      {isLoading && (
        <Card>
          <CardContent className="space-y-3 p-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Empty */}
      {!isLoading && !isError && filtered.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-4 px-6 py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
              <FolderOpen className="h-7 w-7" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-text-primary">
                {hasActiveFilters ? "No matching projects" : "No projects yet"}
              </h3>
              <p className="max-w-sm text-sm text-text-secondary">
                {hasActiveFilters
                  ? "Try adjusting your search or filters to find what you’re looking for."
                  : "Get started by creating your first delivery project."}
              </p>
            </div>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Content */}
      {!isLoading && !isError && filtered.length > 0 && (
        <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <SortHeader
                  label="Project"
                  column="title"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                  className={TITLE_COLUMN_CLASS}
                />
                <SortHeader
                  label="Phase"
                  column="current_phase"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Type"
                  column="project_type"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  label="PM"
                  column="project_manager"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  label="SA"
                  column="solution_architect"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Burn Rate"
                  column="burn_rate_pct"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                  className="w-44"
                />
                <SortHeader
                  label="Status"
                  column="status"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow
                  key={p.id}
                  onClick={() => navigate(`/projects/${p.jira_key}`)}
                  className="cursor-pointer"
                >
                  <TableCell className={TITLE_COLUMN_CLASS}>
                    <div className="flex flex-col">
                      {/* Clamped to 2 lines; the full title stays reachable on hover AND keyboard
                          focus. A native `title` alone never fires on focus, so the span is also a
                          focus stop wrapped in the design-system Tooltip (same pattern as the
                          Intakes Received cell, INT-05). The wrapper is forced back to `block` —
                          Tooltip's default `inline-flex` would make the title a flex item and
                          blockify away the `-webkit-box` display that `line-clamp-2` needs. */}
                      <Tooltip text={p.title} className="block max-w-full">
                        <span
                          tabIndex={0}
                          title={p.title}
                          className="line-clamp-2 rounded font-medium text-text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                        >
                          {p.title}
                        </span>
                      </Tooltip>
                      <span className="font-mono text-xs text-text-secondary">
                        {p.jira_key}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={phaseVariant(p.current_phase)}>
                      {phaseLabel(p.current_phase)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-text-secondary">
                    {projectTypeLabel(p.project_type)}
                  </TableCell>
                  <TableCell className="text-text-secondary">
                    {p.project_manager || "—"}
                  </TableCell>
                  <TableCell className="text-text-secondary">
                    {p.solution_architect || "—"}
                  </TableCell>
                  <TableCell>
                    <BurnRateCell pct={p.burn_rate_pct} />
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(p.status)}>
                      {p.status ? statusLabel(p.status) : "N/A"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default ProjectsPage;
